/** 
 * @namespace CountrySelect
 * @memberof Promega.Shell
 * @description Methods for changing the user's current country context, as well as for showing and hiding the country select modal dialog.
 */

import removeQueryStringParameter from '../util/removeQueryStringParameter';
import getTestEnvironment from '../util/getTestEnvironment';
import trackEvent from '../util/trackEvent';
import { show as showModal, lock as lockModal, unlock as unlockModal } from '../components/modal.js';

const FLAG_IMAGE_CLASS = "country-select-dialog__flag-image",
    COUNTRY_SELECT_CLASS = "country-select-dialog",
    COUNTRY_CONTAINER_CLASS = "country-select-dialog__country-container";

let countrySelectDialog,
    loadedCountryFlags = false,
    loading = false;

// Private functions/listeners

function getCountrySelectFields() {
    const items = [];
    items.push(document.querySelector(".country-select-dialog__dropdown-list"));
    items.push(document.querySelector(".country-select-dialog__dropdown-continue-button"));
    items.push(...Array.from(document.querySelectorAll(".country-select-dialog__language-link")));
    return items.filter(current => current != null);
}

function showLoadingUI() {
    loading = true;

    const itemsToDisable = getCountrySelectFields();

    for (const currentEl of itemsToDisable) {
        currentEl.setAttribute("disabled", "disabled");
    }

    const loadingIndicator = document.querySelector(".country-select-dialog__loading");
    if (loadingIndicator)
        loadingIndicator.classList.add("loading-indicator--visible");

    const continueButton = document.querySelector(".country-select-dialog__dropdown-continue-button");
    if (continueButton)
        continueButton.setAttribute("hidden", "hidden");

    lockModal();
}

function hideLoadingUI() {
    loading = false;

    const itemsToEnable = getCountrySelectFields();

    for (const currentEl of itemsToEnable) {
        currentEl.removeAttribute("disabled");
    }

    const loadingIndicator = document.querySelector(".country-select-dialog__loading");
    if (loadingIndicator)
        loadingIndicator.classList.remove("loading-indicator--visible");

    const continueButton = document.querySelector(".country-select-dialog__dropdown-continue-button");
    if (continueButton)
        continueButton.removeAttribute("hidden");

    updateCountryDropdownEnabled();
    unlockModal();
}

function countrySelectTriggerClicked (event) {
    if (event.target.classList.contains("trigger--country-select")) {
        let eventSource = "Trigger element";
        if (event.target.closest(".alert-container"))
            eventSource = "Cookie policy alert dialog";
        else if (event.target.closest(".site-footer"))
            eventSource = "Site footer";
        else if (event.target.closest(".site-header"))
            eventSource = "Site header";
        showCountrySelect();
        trackEvent("Components", "Country select - opened", eventSource);
        event.preventDefault();
    }
}

function languageLinkClicked (event) {
    if (event.target.classList.contains("country-select-dialog__language-link")) {
        event.preventDefault();
        changeCountry(event.target.getAttribute("data-country-id"), event.target.getAttribute("data-country-lang"));
        trackEvent("Components", "Country select - changed country or language", "Language link");
    }
}

function countryChangeSuccessful (destinationURL) {
    // Server resonse appears to be hard-coded with JSONP callback. Parse the string for a URL.
    let parsedDestination = destinationURL.split('"')[3];
    if (parsedDestination) window.location.href = parsedDestination;
    else hideLoadingUI();
}

function countryChangeFailed () {
    hideLoadingUI();
}

function updateCountryDropdownEnabled() {
    const dropdownEl = document.querySelector(".country-select-dialog__dropdown-list");
    const radioBtnEl = document.querySelector('input[name="globalLanguage"]:checked');
    const continueButton = document.querySelector(".country-select-dialog__dropdown-continue-button");

    if (dropdownEl && continueButton) {
        if ((dropdownEl.value === "Select") || (!radioBtnEl.checked)) {
            continueButton.setAttribute("disabled", "disabled");
        }
        else {
            continueButton.removeAttribute("disabled");
        }
    }
}

function countryDropdownContinueButtonClicked() {
    const countrySelected = document.getElementsByClassName("country-select-dialog__dropdown-list")[0].value;
    const languageSelected = document.querySelector('input[name="globalLanguage"]:checked').value;

    changeCountry(countrySelected, languageSelected);
    trackEvent("Components", "Country select - changed country or language", "Dropdown");
}

function loadCountryFlags() {
    if (!loadedCountryFlags) {
        loadedCountryFlags = true;
        const flagImages = countrySelectDialog.getElementsByClassName(FLAG_IMAGE_CLASS);

        for (let i = 0; i < flagImages.length; i++) {
            const useElement = flagImages[i].firstElementChild,
                srcPath = flagImages[i].getAttribute("data-src");

            if (useElement && srcPath) {
                useElement.setAttributeNS("http://www.w3.org/1999/xlink", "href", srcPath);
            }
        }
    }
}

// API

/**
 * Shows the country select modal dialog.
 */
function showCountrySelect () {
    const countrySelectEl = document.querySelector(`.${COUNTRY_SELECT_CLASS}`);
    if (countrySelectEl) {
        showModal(countrySelectEl);
        loadCountryFlags();
    }
}

/**
 * @function changeCountry
 * @memberof Promega.Shell.CountrySelect
 * @description Requests a country change to the specified country and language code.
 * @param {String} destinationCountry Sitecore item ID of the country to which to change
 * @param {String} destinationLanguage A .NET culture code representing the language to which to change
 */
export function changeCountry(destinationCountry, destinationLanguage) {
    if (!loading) {
        let currentEnv = getTestEnvironment();
        if (currentEnv.length > 0) {
            switch (currentEnv) {
                case "local":
                case "dev":
                case "stage":
                case "test":
                case "testcontent":
                case "content":
                case "cm-testcloud":
                case "stagecontent":
                    currentEnv += ".";
                    break;
                default:
                    currentEnv += "-";
                    break;
            }
        }

        let countryLangQuery = location.protocol + "//" + currentEnv + "worldwide.promega.com/services/setCountry.ashx?countryid=" + destinationCountry;
        if (destinationLanguage) countryLangQuery += "&lang=" + destinationLanguage;
        if (!document.querySelector('meta[content="product"]')) {
            countryLangQuery += "&returnUrl=" + encodeURIComponent(removeQueryStringParameter(document.location.href, "cs"));
        }

        fetch(countryLangQuery, { credentials: "include" }).then((response) => response.text()).then(countryChangeSuccessful).catch(countryChangeFailed);
        showLoadingUI();
    }
}

// Init code

if (typeof document !== "undefined") {
    countrySelectDialog = document.getElementsByClassName(COUNTRY_SELECT_CLASS)[0];
    document.body.addEventListener("click", countrySelectTriggerClicked);
    document.body.addEventListener("touchstart", countrySelectTriggerClicked);

    if (countrySelectDialog) {
        countrySelectDialog.addEventListener("click", languageLinkClicked);
        countrySelectDialog.querySelector(".country-select-dialog__dropdown-list").addEventListener("change", updateCountryDropdownEnabled);
        countrySelectDialog.querySelector(".country-select-dialog__dropdown-continue-button").addEventListener("click", countryDropdownContinueButtonClicked);
    }
    else {
        // Country select dialog is not included on the page. We may be logged in or punched out.
        // In both situations, there should only be one country container available so we can just attach the listener to that.
        const countryContainer = document.querySelector(`.${COUNTRY_CONTAINER_CLASS}`);
        if (countryContainer) countryContainer.addEventListener("click", languageLinkClicked);
    }
}